import { Loading } from '@src/common/Loading';
import { styled } from '@uhg-abyss/web/tools/styled';
import React, { useState } from 'react';
import { Button } from '@src/common/Button';
import { useForm } from '@uhg-abyss/web/hooks/useForm';
import { getErrorFields } from '@src/state/tinRegistrationStatus/utils';
import { useTinRegistrationStatus } from '@src/state/tinRegistrationStatus';
import { config } from '@uhg-abyss/web/tools/config';
import { useRouter } from '@uhg-abyss/web/hooks/useRouter';
import { useUser } from '@src/state/user/hooks';
import { event } from '@uhg-abyss/web/tools/event';
import { PropTypes } from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import { storage } from '@uhg-abyss/web/tools/storage';
import { Grid } from '@uhg-abyss/web/ui/Grid';
import { Card } from '@uhg-abyss/web/ui/Card';
import { SelectRegistrationSearchCriteria } from '../../Landing/TinRegistrationStatus/Inputs/SelectRegistrationSearchCriteria';
import { ByTin } from '../../Landing/TinRegistrationStatus/Inputs/ByTin';
import { ByRequestId } from '../../Landing/TinRegistrationStatus/Inputs/ByRequestId';
import { ByTrackingNumber } from '../../Landing/TinRegistrationStatus/Inputs/ByTrackingNumber';

const MarginStyle = styled('div', {
  margin: '10px 10px 10px 0.825rem',
});

const PageButtons = styled('div', {
  width: '27%',
  paddingTop: '20px',
  paddingBottom: '21px',
  fontWeight: 600,
  fontSize: '16px',
  color: '$primary1',
  fontFamily: '$heading',
});

export const FindAnotherTin = ({ setTinRegStatusError }) => {
  const form = useForm();
  const router = useRouter();
  const [isTin, setIsTin] = useState(false);
  const [isRequestId, setRequestId] = useState(false);
  const [isTrackingNum, setIsTrackingNum] = useState(false);
  const userInfo = useUser.state();
  const isSuperUser = storage.local.get(userInfo.uuid);
  const { searchRegistrationStatus } = useTinRegistrationStatus.actions();
  const { searchRegistrationStatusSaga } = useTinRegistrationStatus.state();

  const resetForm = () => {
    form.reset();
    setIsTin(false);
    setRequestId(false);
    setIsTrackingNum(false);
    setTinRegStatusError(false);
  };
  const errorMessage = 'We were unable to retrieve your TIN registration. ';

  const handleCheckStatus = () => {
    setTinRegStatusError(false);
    form.validate(
      'findAnotherTin',
      (values) => {
        const { registrationOption, tin, requestId, trackingNumber } = values;
        event('START_TIN_REGISTRATION_STATUS', { value: registrationOption });
        searchRegistrationStatus(
          {
            isSuperUser: config('US3671382_SUPER_USER') && !!isSuperUser,
            isAllSelected: registrationOption === 'isAllSelected',
            billingTin: registrationOption === 'tin' ? tin : null,
            requestId: registrationOption === 'requestId' ? requestId : null,
            trackingNumber:
              registrationOption === 'referenceNumber' ? trackingNumber : null,
          },
          (response) => {
            if (registrationOption === 'isAllSelected') {
              router.navigate('/multitin-registration-status');
            } else {
              resetForm();
            }
          },
          (error) => {
            event('API_TRANSACTION_ERROR', {
              code: error.faultCode,
              message: errorMessage + error.message,
            });
            setTinRegStatusError(true);
          }
        );
      },
      (error) => {
        const fields = getErrorFields(error);
        if (!isEmpty(fields)) {
          event('FORM_VALIDATION_ERROR', { fields });
        }
      }
    );
  };
  const handleOption = (selected) => {
    setIsTin(selected.tin);
    setRequestId(selected.requestId);
    setIsTrackingNum(selected.trackingNum);
  };

  return (
    <Loading
      isLoading={searchRegistrationStatusSaga.isLoading}
      style={{ height: '100%' }}
    >
      <Card header="Find another TIN">
        <MarginStyle>
          <p id="find-another-tin-desc">
            If you need to know the submission status for another TIN, you can
            search for it below.
          </p>
          <Grid>
            <Grid.Col span="50%">
              <SelectRegistrationSearchCriteria
                selectedOption={handleOption}
                autoComplete="off"
                inputModel="findAnotherTin.registrationOption"
                inputLabel="findAnotherTin-criteria-input"
              />
            </Grid.Col>
            <Grid.Col span="50%">
              {isTin && (
                <ByTin
                  inputModel="findAnotherTin.tin"
                  id="findAnotherTin-tin"
                />
              )}
              {isRequestId && (
                <ByRequestId
                  inputModel="findAnotherTin.requestId"
                  id="findAnotherTin-requestId"
                />
              )}
              {isTrackingNum && (
                <ByTrackingNumber
                  inputModel="findAnotherTin.trackingNumber"
                  id="findAnotherTin-trackingNumber"
                  required
                />
              )}
            </Grid.Col>
          </Grid>
          <PageButtons>
            <Button
              type="button"
              id="checkstatus-button"
              aria-label="click here to check TIN registration status"
              data-testid="start-button"
              onClick={handleCheckStatus}
              width="100%"
              variant="outline"
            >
              Check Status
            </Button>
          </PageButtons>
        </MarginStyle>
      </Card>
    </Loading>
  );
};

FindAnotherTin.propTypes = {
  setTinRegStatusError: PropTypes.func.isRequired,
};

FindAnotherTin.defaultProps = {};
